import Stylus from './Stylus';
import ShapeDrawingBrush, {
  type StylusShapeOptions,
} from './ShapeDrawingBrush';
import { type ShapeName, shapeNameToClass } from '../shapes';
import type WhiteboardController from '../controllers/WhiteboardController';
import styles from '../styles.module.css';
import { labeledMenuComponent } from '../utils/stylus';
import { shapeNameToDescription } from '../objects/Shape';
import type ShapeBase from '../shapes/ShapeBase';
import { type ComponentClass } from 'react';
import { ADVANCED_SHAPES } from '../utils/shapes';
import { BASIC_SHAPES } from '../controllers/WhiteboardController';

// Non brush shapes are ones where a modal pops up when you select it and so you
// wouldn't be able to use it like a brush to put down multiple of them.
const NON_BRUSH_SHAPES = ['image', 'record'] as const;

const LABELED_SHAPES = ['smartRecord', 'record', ...ADVANCED_SHAPES] as const;

// Create new shape objects.
class StylusShape extends Stylus {
  shapeName: ShapeName;
  public options: StylusShapeOptions = {};
  shapeClass: typeof ShapeBase;

  constructor({
    controller,
    shapeName,
    initialOptions = {},
  }: {
    controller: WhiteboardController;
    shapeName: ShapeName;
    initialOptions?: StylusShapeOptions;
  }) {
    super({ controller });
    this.shapeName = shapeName || 'rect';
    this.shapeClass = shapeNameToClass(this.shapeName);
    this.options = initialOptions;
  }

  stylusId() {
    return `${this.shapeName}${
      this.options.recordType ? `_${this.options.recordType}` : ''
    }`;
  }

  activate() {
    if (this.nonBrushShape()) {
      this.controller.actionUpdateStylusLock();
      this.controller.actionAddExternalShape(this.shapeName);
      this.drawingComplete();
    } else {
      const brush = new ShapeDrawingBrush(
        this.controller.canvas,
        this,
        this.options
      );
      this.controller.canvas.isDrawingMode = true;
      this.controller.canvas.freeDrawingBrush = brush;
    }
  }

  renderIcon() {
    return this.shapeClass.renderIcon();
  }

  renderMenuIcon() {
    return this.shapeClass.renderMenuIcon(this.options);
  }

  toolButtonClass() {
    if (this.shapeName === 'spriteIcon') {
      return styles.iconToolButton;
    }
    if (LABELED_SHAPES.includes(this.shapeName)) {
      return styles.labeledToolButton;
    }
  }

  menuComponent() {
    if (LABELED_SHAPES.includes(this.shapeName)) {
      return labeledMenuComponent(this.renderMenuIcon(), this.description());
    }
  }

  nonBrushShape() {
    return NON_BRUSH_SHAPES.includes(this.shapeName);
  }

  cursor(): string {
    if (this.shapeName === 'text') {
      return 'text';
    }

    return 'crosshair';
  }

  subMenu(): ComponentClass<any, any> | React.FC | null {
    return null;
  }

  subMenuIncludes(shapeName) {
    // TODO: This only includes basic and advanced shapes. It is missing all
    // the other shapes in the shapes menu. This function should be pulled into
    // a controller that can compare it against the true set of shapes in the
    // menu.
    const allShapes = BASIC_SHAPES.concat(ADVANCED_SHAPES);
    return allShapes.includes(shapeName);
  }

  subMenuName(): string | null {
    if (this.subMenuIncludes(this.shapeName)) {
      return 'shapes';
    } else {
      return null;
    }
  }

  description() {
    return (
      this.shapeClass.getDescription(this.options) ||
      shapeNameToDescription(this.shapeName)
    );
  }
}

export default StylusShape;
